/**
 * Calculates logarithmically distributed FFT bin indices for frequency visualization.
 *
 * Human hearing is logarithmic - each octave spans a doubling of frequency.
 * This function maps screen x-positions to FFT bins using a logarithmic scale,
 * giving more visual space to bass frequencies and less to treble.
 */

export interface LogFrequencyOptions {
  fftSize: number;
  sampleRate: number;
  numPoints?: number;
  minFreq?: number;
  maxFreq?: number;
}

/**
 * Generate an array of FFT bin indices distributed logarithmically across a frequency range.
 *
 * @param options Configuration options
 * @param options.fftSize FFT size (e.g., 8192)
 * @param options.sampleRate Audio sample rate (e.g., 44100 or 48000)
 * @param options.numPoints Number of points to sample (default: 1024)
 * @param options.minFreq Minimum frequency in Hz (default: 40)
 * @param options.maxFreq Maximum frequency in Hz (default: 16000)
 * @returns Array of bin indices to sample for logarithmic distribution
 */
export function getLogFrequencyBins({
  fftSize,
  sampleRate,
  numPoints = 1024,
  minFreq = 40,
  maxFreq = 16000,
}: LogFrequencyOptions): number[] {
  const nyquist = sampleRate / 2;
  const binCount = fftSize / 2;
  const freqPerBin = nyquist / binCount;

  const bins: number[] = [];
  const logMin = Math.log10(minFreq);
  const logMax = Math.log10(maxFreq);
  const logRange = logMax - logMin;

  for (let i = 0; i < numPoints; i++) {
    // Normalize position from 0 to 1
    const t = i / (numPoints - 1);

    // Calculate logarithmic frequency
    const logFreq = logMin + t * logRange;
    const freq = Math.pow(10, logFreq);

    // Convert frequency to bin index
    const bin = Math.floor(freq / freqPerBin);

    // Clamp to valid range
    bins.push(Math.min(bin, binCount - 1));
  }

  return bins;
}

/**
 * Creates a reusable frequency bin mapper function with fixed parameters.
 *
 * @param options Configuration options (same as getLogFrequencyBins)
 * @returns A function that returns the bin indices array
 */
export function createLogFrequencyMapper(options: LogFrequencyOptions): () => number[] {
  const bins = getLogFrequencyBins(options);
  return () => bins;
}
